// src/components/streams/EditStream.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { doc, getDoc, writeBatch, serverTimestamp } from 'firebase/firestore';
import { db, functions } from '../../firebaseConfig';
import { useAuthUser } from '../../hooks/useAuthUser';
import { httpsCallable } from "firebase/functions";
import { FiArrowLeft, FiSave, FiLoader, FiAlertCircle, FiSettings, FiClock, FiTag, FiType, FiMessageSquare, FiTrash2, FiImage } from 'react-icons/fi';
import { useToast } from '../shared/contexts/ToastContext';
import { useConfirmDialog } from '../shared/contexts/ConfirmContext';
import { TAXONOMY_MIRROR, SMART_AUTO_VIBE } from '../shared/constants/taxonomyMirror';

// --- Define the Cloud Functions ---
const deleteStreamFn = httpsCallable(functions, 'deleteStream');

function EditStream() {
    const { projectId, streamId } = useParams();
    const { userId } = useAuthUser();
    const navigate = useNavigate();
    const { addToast } = useToast();
    const { confirm } = useConfirmDialog();

    // --- State ---
    const [loading, setLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [error, setError] = useState('');
    
    const [formData, setFormData] = useState({
        streamName: '',
        frequency: 'daily',
        scheduleTime: '10:00',
        contentPlanId: '',
        visualSettings: {
            categoryId: TAXONOMY_MIRROR[0].id,
            vibeId: TAXONOMY_MIRROR[0].vibes[0].id,
        },
        plan: {
            refinedTopic: '',
            angle: '',
            suggestedCTA: ''
        }
    });

    // --- Fetch Stream Data ---
    const fetchStreamData = useCallback(async () => {
        if (!userId || !projectId || !streamId) {
            setError("Invalid request parameters.");
            setLoading(false);
            return;
        }
        setLoading(true);

        try {
            const streamRef = doc(db, 'projects', projectId, 'contentStreams', streamId);
            const streamSnap = await getDoc(streamRef);

            if (!streamSnap.exists() || streamSnap.data().userId !== userId) {
                throw new Error("Campaign not found or you do not have permission to access it.");
            }
            const streamData = streamSnap.data();
            const planId = streamData.contentPlanId;

            if (!planId) {
                throw new Error("This campaign is missing its content plan link. Cannot edit.");
            }

            const planRef = doc(db, 'contentPlans', planId);
            const planSnap = await getDoc(planRef);

            if (!planSnap.exists()) {
                setError("Warning: The AI Generation Plan data is missing, but you can still edit the campaign name.");
                const vs = streamData.visualSettings || {};
                setFormData({
                    streamName: streamData.streamName || '',
                    frequency: streamData.frequency || 'daily',
                    scheduleTime: streamData.scheduleTime || '10:00',
                    contentPlanId: planId,
                    visualSettings: {
                        ...vs,
                        categoryId: vs.categoryId || TAXONOMY_MIRROR[0].id,
                        vibeId: vs.vibeId || TAXONOMY_MIRROR[0].vibes[0].id,
                    },
                    plan: { refinedTopic: '', angle: '', suggestedCTA: '' }
                });
            } else {
                 const planData = planSnap.data();
                 const vs = streamData.visualSettings || {};
                 setFormData({
                    streamName: streamData.streamName || '',
                    frequency: streamData.frequency || 'daily',
                    scheduleTime: streamData.scheduleTime || '10:00',
                    contentPlanId: planId,
                    visualSettings: {
                        ...vs,
                        categoryId: vs.categoryId || TAXONOMY_MIRROR[0].id,
                        vibeId: vs.vibeId || TAXONOMY_MIRROR[0].vibes[0].id,
                    },
                    plan: {
                        refinedTopic: planData.plan?.refinedTopic || '',
                        angle: planData.plan?.angle || '',
                        suggestedCTA: planData.plan?.suggestedCTA || ''
                    }
                });
            }
        } catch (err) {
            setError("Failed to load campaign data. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [userId, projectId, streamId]);

    useEffect(() => {
        fetchStreamData();
    }, [fetchStreamData]);

    // --- Input Handlers ---
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePlanChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            plan: {
                ...prev.plan,
                [name]: value
            }
        }));
    };
    
    // --- Save/Delete Handlers ---
    const handleSaveChanges = async (e) => {
        e.preventDefault();
        if (!formData.contentPlanId) {
            setError("Cannot save, content plan ID is missing.");
            return;
        }

        setIsSaving(true);
        setError('');

        try {
            const batch = writeBatch(db);
            
            const streamRef = doc(db, 'projects', projectId, 'contentStreams', streamId);
            batch.update(streamRef, {
                streamName: formData.streamName,
                frequency: formData.frequency,
                scheduleTime: formData.scheduleTime,
                visualSettings: formData.visualSettings,
                updatedAt: serverTimestamp()
            });
            
            const planRef = doc(db, 'contentPlans', formData.contentPlanId);
            batch.update(planRef, {
                "plan.refinedTopic": formData.plan.refinedTopic,
                "plan.angle": formData.plan.angle,
                "plan.suggestedCTA": formData.plan.suggestedCTA,
            });

            await batch.commit();
            addToast('Campaign updated! AI plan changes will apply to the next generated batch.', 'success');

        } catch (err) {
            const errorMessage = "Failed to save changes. Please try again.";
            setError(errorMessage);
            addToast(errorMessage, 'error');
        } finally {
            setIsSaving(false);
        }
    };

    const handleDeleteStream = async () => {
        const confirmed = await confirm({
            title: 'Delete Campaign',
            message: 'Are you sure you want to permanently delete the "' + formData.streamName + '" campaign?\n\nThis will delete the content plan, all associated posts (past and future), and any uploaded visuals. This action cannot be undone.',
            confirmLabel: 'Delete',
            variant: 'danger'
        });
        if (!confirmed) return;

        setIsDeleting(true);
        setError('');

        try {
            await deleteStreamFn({ projectId, streamId });
            
            navigate(`/project/${projectId}`, {
                replace: true,
                state: { message: `Campaign "${formData.streamName}" was successfully deleted.` }
            });
            setIsDeleting(false);

        } catch (err) {
            setError("Campaign deletion failed. Please try again.");
            addToast("Campaign deletion failed. Please try again.", 'error');
            setIsDeleting(false);
        }
    };

    // --- Render Logic ---
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen bg-warm-dark">
                <FiLoader className="animate-spin text-4xl text-slate-blue" />
            </div>
        );
    }
    
    if (error && !formData.streamName) {
        return (
            <div className="max-w-4xl mx-auto p-6 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-200 mt-10 backdrop-blur-sm">
                <div className="flex items-center">
                    <FiAlertCircle className="h-6 w-6 mr-3 text-red-400" />
                    <h2 className="text-xl font-bold">Error</h2>
                </div>
                <p className="mt-2 text-white/80">{error}</p>
                <Link to={`/project/${projectId}`} className="mt-4 inline-block text-slate-blue hover:text-cream hover:underline transition-colors">
                    ← Back to Project Overview
                </Link>
            </div>
        );
    }

    return (
        <div className="bg-warm-dark min-h-full py-8 text-cream">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
                <Link to={`/project/${projectId}`} className="inline-flex items-center text-sm font-medium text-cream/50 hover:text-white transition-colors">
                    <FiArrowLeft className="mr-2 h-4 w-4" />
                    Back to Project Overview
                </Link>

                <form onSubmit={handleSaveChanges} className="bg-warm-gray/10 backdrop-blur-md rounded-[2rem] shadow-xl border border-white/5 overflow-hidden">
                    <div className="p-8 border-b border-white/5">
                        <div className="flex items-center">
                            <div className="p-3 bg-slate-blue/20 rounded-xl mr-4">
                                <FiSettings className="h-6 w-6 text-slate-blue" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-cream tracking-tight">Edit Recurring Campaign</h1>
                                <p className="text-sm text-cream/50 mt-1 font-medium">Changes will affect all future posts generated by this campaign.</p>
                            </div>
                        </div>
                    </div>
                    
                    {error && <div className="p-6 border-b border-red-500/20 text-sm text-red-200 bg-red-500/10 flex items-center gap-2"><FiAlertCircle/> {error}</div>}

                    <div className="p-8 space-y-8">
                        <div>
                            <label htmlFor="streamName" className="block text-xs font-black text-cream/40 uppercase tracking-widest mb-2 flex items-center">
                                <FiType className="mr-2 text-slate-blue" /> Campaign Name
                            </label>
                            <input
                                type="text" name="streamName" id="streamName" value={formData.streamName} onChange={handleInputChange}
                                className="mt-1 block w-full bg-black/40 border border-white/5 rounded-2xl text-cream focus:border-slate-blue focus:ring-1 focus:ring-slate-blue p-4 transition-all shadow-inner" required
                            />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            <div>
                                <label htmlFor="frequency" className="block text-sm font-bold text-cream/40 uppercase tracking-widest mb-2 flex items-center">
                                    <FiClock className="mr-2 !text-slate-blue h-4 w-4" style={{ color: '#7B8FA1' }} /> Frequency
                                </label>
                                <select 
                                    id="frequency" 
                                    name="frequency" 
                                    value={formData.frequency} 
                                    onChange={handleInputChange} 
                                    className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream"
                                >
                                    <option value="daily">Daily</option>
                                    <option value="every_second_day">Every Second Day</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-cream/40 uppercase tracking-widest mb-2 flex items-center">
                                    <FiClock className="mr-2 !text-slate-blue h-4 w-4" style={{ color: '#7B8FA1' }} /> Post Time (UTC)
                                </label>
                                <div className="flex gap-2">
                                    <select 
                                        className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-1/2"
                                        value={formData.scheduleTime.split(':')[0]}
                                        onChange={(e) => setFormData(prev => ({ ...prev, scheduleTime: `${e.target.value}:${prev.scheduleTime.split(':')[1] || '00'}` }))}
                                    >
                                        {Array.from({ length: 24 }).map((_, i) => (
                                            <option key={i} value={i.toString().padStart(2, '0')}>{i.toString().padStart(2, '0')}</option>
                                        ))}
                                    </select>
                                    <select 
                                        className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-1/2"
                                        value={formData.scheduleTime.split(':')[1]}
                                        onChange={(e) => setFormData(prev => ({ ...prev, scheduleTime: `${prev.scheduleTime.split(':')[0] || '10'}:${e.target.value}` }))}
                                    >
                                        {Array.from({ length: 12 }).map((_, i) => (
                                            <option key={i*5} value={(i*5).toString().padStart(2, '0')}>{(i*5).toString().padStart(2, '0')}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>
                        {/* --- Visual Style Section --- */}
                        <div className="space-y-4 pt-6 border-t border-white/5">
                            <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-lg font-bold text-cream">Visual Style</h3>
                                <div className="h-px flex-1 bg-white/5"></div>
                            </div>
                            <p className="text-xs text-cream/40 -mt-2 uppercase tracking-widest font-bold">Changes apply to next batch</p>
                            <div>
                                <label className="block text-xs font-black text-cream/40 uppercase tracking-widest mb-2 flex items-center">
                                    <FiImage className="mr-2 text-slate-blue" /> Category
                                </label>
                                <select
                                    value={formData.visualSettings.categoryId}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === 'smart_auto') {
                                            setFormData(prev => ({
                                                ...prev,
                                                visualSettings: { categoryId: 'professional', vibeId: 'smart_auto' }
                                            }));
                                        } else {
                                            const cat = TAXONOMY_MIRROR.find(c => c.id === val);
                                            setFormData(prev => ({
                                                ...prev,
                                                visualSettings: {
                                                    ...prev.visualSettings,
                                                    categoryId: val,
                                                    vibeId: cat ? cat.vibes[0].id : prev.visualSettings.vibeId,
                                                }
                                            }));
                                        }
                                    }}
                                    className="form-select text-sm py-3 rounded-xl bg-warm-gray/20 text-cream w-full"
                                >
                                    <option value="smart_auto">{SMART_AUTO_VIBE.label}</option>
                                    {TAXONOMY_MIRROR.map(cat => (
                                        <option key={cat.id} value={cat.id}>{cat.label}</option>
                                    ))}
                                </select>
                            </div>
                            {formData.visualSettings.vibeId !== 'smart_auto' && (
                            <div>
                                <label className="block text-xs font-black text-cream/40 uppercase tracking-widest mb-3 flex items-center">
                                    <FiImage className="mr-2 text-slate-blue" /> Vibe
                                </label>
                                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                    {(TAXONOMY_MIRROR.find(c => c.id === formData.visualSettings.categoryId)?.vibes || []).map(vibe => (
                                        <button
                                            key={vibe.id}
                                            type="button"
                                            onClick={() => setFormData(prev => ({
                                                ...prev,
                                                visualSettings: { ...prev.visualSettings, vibeId: vibe.id }
                                            }))}
                                            className={`p-3 rounded-xl border text-left transition-all ${
                                                formData.visualSettings.vibeId === vibe.id
                                                    ? 'border-slate-blue bg-slate-blue/20 ring-1 ring-slate-blue'
                                                    : 'border-white/5 bg-black/20 hover:border-white/20'
                                            }`}
                                        >
                                            <span className={`text-sm font-bold block ${
                                                formData.visualSettings.vibeId === vibe.id ? 'text-slate-blue' : 'text-cream'
                                            }`}>{vibe.label}</span>
                                            <span className="text-xs text-cream/40 line-clamp-2">{vibe.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            )}
                        </div>

                        <div className="space-y-6 pt-6 border-t border-white/5">
                             <div className="flex items-center gap-2 mb-2">
                                <h3 className="text-lg font-bold text-cream">AI Generation Plan</h3>
                                <div className="h-px flex-1 bg-white/5"></div>
                             </div>
                             <p className="text-xs text-cream/40 -mt-2 uppercase tracking-widest font-bold">Changes apply to next batch</p>
                            <div>
                                <label htmlFor="refinedTopic" className="block text-xs font-black text-cream/40 uppercase tracking-widest mb-2 flex items-center">
                                    <FiMessageSquare className="mr-2 text-slate-blue" /> Core Topic / Inspiration
                                </label>
                                <textarea id="refinedTopic" name="refinedTopic" rows={3} value={formData.plan.refinedTopic} onChange={handlePlanChange} className="mt-1 block w-full bg-black/40 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:ring-1 focus:ring-slate-blue focus:border-slate-blue p-4 transition-all shadow-inner" placeholder="e.g., 'Tips for beginner photographers'" />
                            </div>
                            <div>
                                <label htmlFor="angle" className="block text-xs font-black text-cream/40 uppercase tracking-widest mb-2 flex items-center">
                                    <FiMessageSquare className="mr-2 text-slate-blue" /> Content Angle
                                </label>
                                <input type="text" name="angle" id="angle" value={formData.plan.angle} onChange={handlePlanChange} className="mt-1 block w-full bg-black/40 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:ring-1 focus:ring-slate-blue focus:border-slate-blue p-4 transition-all shadow-inner" placeholder="e.g., 'Focus on using natural light effectively'" />
                            </div>
                            <div>
                                <label htmlFor="suggestedCTA" className="block text-xs font-black text-cream/40 uppercase tracking-widest mb-2 flex items-center">
                                    <FiTag className="mr-2 text-slate-blue" /> Suggested Call to Action
                                </label>
                                <input type="text" name="suggestedCTA" id="suggestedCTA" value={formData.plan.suggestedCTA} onChange={handlePlanChange} className="mt-1 block w-full bg-black/40 border border-white/5 rounded-2xl text-cream placeholder-cream/20 focus:ring-1 focus:ring-slate-blue focus:border-slate-blue p-4 transition-all shadow-inner" placeholder="e.g., 'Share your best photo in the comments!'" />
                            </div>
                        </div>

                        {/* --- Automation Info Section --- */}

                    </div>
                    
                    <div className="bg-black/20 px-8 py-6 border-t border-white/5 flex items-center justify-end">
                        <button type="submit" disabled={isSaving || isDeleting} className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-bold rounded-xl shadow-glow text-warm-dark bg-slate-blue hover:bg-slate-blue/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-slate-blue disabled:opacity-50 transition-all hover:scale-105 active:scale-95 uppercase tracking-wider">
                            {isSaving ? (<><FiLoader className="animate-spin -ml-1 mr-3 h-5 w-5" />Saving...</>) : (<><FiSave className="-ml-1 mr-2 h-5 w-5" />Save Changes</>)}
                        </button>
                    </div>
                </form>

                <div className="mt-8 p-8 bg-red-500/5 rounded-[2rem] border border-red-500/20 backdrop-blur-sm">
                    <h3 className="text-lg font-bold text-red-400 uppercase tracking-widest flex items-center gap-2"><FiAlertCircle/> Danger Zone</h3>
                    <p className="mt-2 text-sm text-red-200/70">
                        Deleting this campaign will permanently remove its content plan, all associated posts (past and future), and any uploaded visuals. This action cannot be undone.
                    </p>
                    <div className="mt-6">
                        <button
                            type="button"
                            onClick={handleDeleteStream}
                            disabled={isDeleting || isSaving}
                            className="inline-flex items-center justify-center px-6 py-3 border border-red-500/30 text-sm font-bold rounded-xl shadow-sm text-red-300 bg-red-500/10 hover:bg-red-500/20 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-all uppercase tracking-wider"
                        >
                            {isDeleting ? (<><FiLoader className="animate-spin -ml-1 mr-3 h-5 w-5" />Deleting...</>) : (<><FiTrash2 className="-ml-1 mr-2 h-5 w-5" />Delete Campaign</>)}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default EditStream;